import React from 'react';
import { Navigation } from '../components/Navigation';
import { ContactForm } from '../components/ContactForm';
import { WorldMap } from '../components/WorldMap';
import {
  Shield,
  Cloud,
  Lock,
  FileText,
  Database,
  Eye,
  Palette,
  BarChart3,
  Server,
  Layers,
  BookOpen,
  Code,
  Accessibility,
  Sparkles,
  TrendingUp,
  GraduationCap,
  Building,
  Landmark,
  Heart,
  Truck,
  ShoppingBag,
  CheckCircle2,
  ArrowRight,
  MapPin,
  Mail as MailIcon,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';

const Home = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  // Core Services Data
  const coreServices = [
    {
      icon: Shield,
      title: 'Identity and Access Management',
      description: 'Modern identity solutions for secure, scalable enterprise access control.',
      tech: 'Microsoft Entra ID, Azure AD B2C, Conditional Access',
    },
    {
      icon: Cloud,
      title: 'Microsoft and Cloud Solutions',
      description: 'End-to-end Microsoft 365 and Azure implementation, migration, and governance.',
      tech: 'M365, Azure, SharePoint, Teams, Power Platform',
    },
    {
      icon: Lock,
      title: 'Security and Infrastructure Support',
      description: 'Comprehensive security architecture and infrastructure management for enterprise stability.',
      tech: 'Zero Trust, MFA, Security Operations, Compliance',
    },
    {
      icon: FileText,
      title: 'Publishing Production Services',
      description: 'Structured editorial workflows and production systems for digital-first publishing.',
      tech: 'Editorial systems, XML workflows, Production pipelines',
    },
    {
      icon: Database,
      title: 'Data Conversion and Digital Formats',
      description: 'Transform legacy content into modern, structured digital formats at scale.',
      tech: 'XML, EPUB3, HTML5, PDF workflows',
    },
    {
      icon: Eye,
      title: 'Accessibility Services',
      description: 'Born-accessible content creation and remediation to meet global standards.',
      tech: 'WCAG 2.2 AA, PDF/UA, EPUB Accessibility 1.1, EAA',
    },
    {
      icon: Palette,
      title: 'Creative and Brand Services',
      description: 'Strategic brand systems, visual identity, and design for multi-channel execution.',
      tech: 'Brand architecture, Design systems, Campaign creative',
    },
    {
      icon: BarChart3,
      title: 'Marketing Operations and Analytics',
      description: 'Data-driven marketing infrastructure and performance measurement systems.',
      tech: 'MarTech stack, Analytics, Attribution, Campaign ops',
    },
  ];

  // Capability Clusters
  const capabilityClusters = [
    {
      icon: Server,
      title: 'Identity, Cloud, and Infrastructure',
      description:
        'If you need to modernise identity, migrate to cloud, or secure and scale your infrastructure, we build the foundation.',
      includes: [
        'Microsoft Entra ID implementation and tenant architecture',
        'Azure and M365 migration, governance, and optimisation',
        'Security operations, Zero Trust, and compliance frameworks',
        'Infrastructure support and managed security services',
      ],
    },
    {
      icon: BookOpen,
      title: 'Publishing, Conversion, and Accessibility',
      description:
        'If you publish content at scale, we help you structure workflows, convert legacy formats, and meet accessibility standards.',
      includes: [
        'Editorial production systems and XML-first workflows',
        'Content conversion from legacy formats to modern standards',
        'Born-accessible and remediated content (WCAG 2.2, PDF/UA, EPUB)',
        'Quality assurance and compliance validation',
      ],
    },
    {
      icon: Sparkles,
      title: 'Creative, Brand, and Marketing Operations',
      description:
        'If you need brand clarity, creative execution, or marketing infrastructure, we bring strategy and systems together.',
      includes: [
        'Brand strategy, identity systems, and design guidelines',
        'Campaign creative and multi-channel asset production',
        'Marketing technology stack setup and integration',
        'Analytics, attribution, and performance tracking',
      ],
    },
  ];

  // Industries
  const industries = [
    {
      icon: GraduationCap,
      name: 'Education & Publishing',
      problem: 'Complex editorial workflows, accessibility mandates, legacy format migrations.',
      solution: 'Structured production pipelines, born-accessible content, modern digital formats.',
    },
    {
      icon: Landmark,
      name: 'Government & Public Sector',
      problem: 'Compliance requirements, security standards, accessibility obligations, legacy systems.',
      solution: 'Secure identity architecture, accessible digital services, structured content systems.',
    },
    {
      icon: Building,
      name: 'BFSI',
      problem: 'Security mandates, regulatory compliance, customer identity complexity, data governance.',
      solution: 'Zero Trust architecture, identity management, secure cloud infrastructure, compliance automation.',
    },
    {
      icon: Heart,
      name: 'Healthcare',
      problem: 'Patient data security, compliance frameworks, accessibility requirements, system integration.',
      solution: 'Secure identity solutions, accessible patient portals, compliant data management.',
    },
    {
      icon: Truck,
      name: 'Logistics & Mobility',
      problem: 'Complex operations, real-time data, partner integrations, customer-facing systems.',
      solution: 'Cloud infrastructure, identity federation, operational dashboards, analytics systems.',
    },
    {
      icon: ShoppingBag,
      name: 'Consumer Brands',
      problem: 'Multi-channel marketing, brand consistency, customer data platforms, attribution.',
      solution: 'Brand systems, marketing operations infrastructure, analytics, campaign execution.',
    },
  ];

  // Selected Work
  const selectedWork = [
    {
      title: 'Enterprise Tenant Modernisation',
      situation: 'Global enterprise with 40,000+ users needed to consolidate fragmented identity systems.',
      action: 'Designed and migrated to unified Microsoft Entra ID architecture with conditional access and governance.',
      result: 'Reduced identity sprawl, improved security posture, enabled modern authentication across all systems.',
    },
    {
      title: 'Born-Accessible Publishing Pipeline',
      situation: 'Leading educational publisher required WCAG 2.2 AA compliance across thousands of digital titles.',
      action: 'Built structured XML-first workflow with automated accessibility validation and remediation.',
      result: '100% compliance achieved, production time reduced, future-proofed for evolving accessibility standards.',
    },
    {
      title: 'Brand Systems for Category Leaders',
      situation: 'Mid-market brand needed cohesive identity system for expansion across new markets and channels.',
      action: 'Developed complete brand architecture, design system, and multi-channel creative guidelines.',
      result: 'Clear brand positioning, consistent execution across touchpoints, foundation for growth.',
    },
    {
      title: 'Lifecycle and Analytics Infrastructure',
      situation: 'B2B SaaS company lacked visibility into customer journey and marketing attribution.',
      action: 'Implemented integrated MarTech stack with lifecycle tracking, attribution modeling, and dashboards.',
      result: 'Clear ROI visibility, optimised campaign performance, data-driven decision making.',
    },
  ];

  return (
    <div className="min-h-screen bg-[#06121E]">
      <Navigation />
      <main>

      {/* 01. HERO SECTION */}
      <section id="hero" className="relative min-h-screen flex items-center overflow-hidden pt-20">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                'url(https://images.unsplash.com/photo-1639066648921-82d4500abf1a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxlbnRlcnByaXNlJTIwY2xvdWQlMjBpbmZyYXN0cnVjdHVyZXxlbnwwfHx8YmxhY2t8MTc3OTE5MzUwNnww&ixlib=rb-4.1.0&q=85)',
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-b from-[#06121E]/60 via-[#06121E]/85 to-[#06121E]"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#06121E] via-transparent to-[#06121E]/80"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32">
          <div className="max-w-5xl">
            <h1 className="text-[#EAF4FF] font-extrabold leading-[1.08] mb-8 tracking-tight fade-in">
              Multidisciplinary practice for enterprise technology, identity, publishing, and creative operations
            </h1>
            <p className="text-xl text-[#A9BDD1] leading-[1.7] mb-12 max-w-3xl fade-in font-light">
              We bring technology, cloud infrastructure, accessible publishing, and marketing systems into one structured
              operating partner. From identity architecture to brand execution, we build foundations that scale.
            </p>
            <div className="flex flex-wrap gap-4 fade-in items-center">
              <Button
                onClick={() => scrollToSection('contact')}
                size="lg"
                className="bg-[#19B5FF] hover:bg-[#48C8FF] text-[#06121E] font-semibold px-8 py-6 text-[15px] rounded-md transition-all duration-300 hover:shadow-lg hover:shadow-[#19B5FF]/40 tracking-tight"
              >
                Schedule a Consultation
              </Button>
              <Button
                onClick={() => scrollToSection('capabilities')}
                size="lg"
                variant="outline"
                className="border border-[rgba(120,170,210,0.4)] bg-transparent text-[#EAF4FF] hover:bg-[#EAF4FF]/5 hover:border-[#19B5FF] font-semibold px-8 py-6 text-[15px] rounded-md transition-all duration-300 tracking-tight"
              >
                Explore Capabilities
              </Button>
            </div>

            {/* Hero meta strip */}
            <div className="mt-16 pt-8 border-t border-[rgba(120,170,210,0.18)] max-w-3xl fade-in">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { label: 'Regions', value: '4' },
                  { label: 'Disciplines', value: '8' },
                  { label: 'Standards', value: 'WCAG · EAA' },
                  { label: 'Approach', value: 'Structured' },
                ].map((stat, i) => (
                  <div key={i}>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-[#19B5FF] font-bold mb-2">{stat.label}</p>
                    <p className="text-[#EAF4FF] text-base font-semibold">{stat.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Editorial Scroll Cue - right edge */}
        <div className="absolute bottom-10 right-8 lg:right-12 z-10 hidden md:flex flex-col items-center gap-3">
          <span className="text-[10px] uppercase tracking-[0.35em] text-[#A9BDD1] font-medium [writing-mode:vertical-rl] rotate-180">Scroll</span>
          <div className="w-px h-12 bg-gradient-to-b from-[#19B5FF] via-[#19B5FF]/40 to-transparent"></div>
        </div>
      </section>

      {/* 02. CORE SERVICES */}
      <section id="services" className="py-32 bg-[#0B1B2B] relative overflow-hidden">
        {/* Background Visual */}
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                'url(https://images.unsplash.com/photo-1551288049-bebda4e38f71)',
            }}
          ></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            <span className="text-[#19B5FF] font-bold text-xs uppercase tracking-[0.2em] mb-4 block">02. Core Services</span>
            <h2 className="text-[#EAF4FF] font-extrabold mt-2 mb-6 leading-[1.1]">Eight Disciplines</h2>
            <p className="text-[#A9BDD1] text-lg max-w-2xl mx-auto leading-relaxed">
              From identity architecture to marketing analytics, our services cover the full spectrum of modern enterprise
              needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreServices.map((service, index) => (
              <Card
                key={index}
                className="bg-[#10263A]/80 backdrop-blur-sm border-[rgba(120,170,210,0.18)] hover:border-[#19B5FF] transition-all duration-300 hover-lift group"
              >
                <CardContent className="p-7">
                  <service.icon className="w-11 h-11 text-[#19B5FF] mb-5 group-hover:scale-110 transition-transform duration-300 stroke-[1.5]" />
                  <h3 className="text-[#EAF4FF] font-bold text-base mb-3 leading-tight">{service.title}</h3>
                  <p className="text-[#A9BDD1] text-sm mb-5 leading-[1.6]">{service.description}</p>
                  <p className="text-[#19B5FF] text-xs font-medium leading-relaxed">{service.tech}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 03. CAPABILITY CLUSTERS */}
      <section id="capabilities" className="py-32 bg-[#06121E] relative overflow-hidden">
        {/* Background Visual */}
        <div className="absolute right-0 top-0 bottom-0 w-1/2 opacity-[0.03]">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                'url(https://images.unsplash.com/photo-1609770003614-6d6156451d5c)',
            }}
          ></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            <span className="text-[#19B5FF] font-bold text-xs uppercase tracking-[0.2em] mb-4 block">03. Capability Clusters</span>
            <h2 className="text-[#EAF4FF] font-extrabold mt-2 mb-6 leading-[1.1]">Three Integrated Practices</h2>
            <p className="text-[#A9BDD1] text-lg max-w-2xl mx-auto leading-relaxed">
              Our disciplines combine into three core capability clusters, each addressing distinct enterprise challenges.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {capabilityClusters.map((cluster, index) => (
              <Card
                key={index}
                className="bg-gradient-to-br from-[#10263A]/90 to-[#0B1B2B]/90 backdrop-blur-sm border-[rgba(120,170,210,0.18)] hover:border-[#19B5FF] transition-all duration-300 hover-lift"
              >
                <CardContent className="p-9">
                  <cluster.icon className="w-14 h-14 text-[#19B5FF] mb-7 stroke-[1.5]" />
                  <h3 className="text-[#EAF4FF] font-bold text-xl mb-5 leading-tight">{cluster.title}</h3>
                  <p className="text-[#A9BDD1] mb-7 leading-[1.7] text-[15px]">{cluster.description}</p>
                  <Separator className="bg-[rgba(120,170,210,0.18)] my-7" />
                  <ul className="space-y-4">
                    {cluster.includes.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm text-[#A9BDD1] leading-[1.6]">
                        <CheckCircle2 className="w-5 h-5 text-[#19B5FF] flex-shrink-0 mt-0.5 stroke-[1.5]" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 04. INDUSTRIES */}
      <section id="industries" className="py-32 bg-[#0B1B2B] relative overflow-hidden">
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#06121E]/20 via-transparent to-[#06121E]/20"></div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            <span className="text-[#19B5FF] font-bold text-xs uppercase tracking-[0.2em] mb-4 block">04. Industries</span>
            <h2 className="text-[#EAF4FF] font-extrabold mt-2 mb-6 leading-[1.1]">Sector Expertise</h2>
            <p className="text-[#A9BDD1] text-lg max-w-2xl mx-auto leading-relaxed">
              Deep experience across education, government, BFSI, healthcare, logistics, and consumer brands.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((industry, index) => (
              <Card
                key={index}
                className="bg-[#10263A]/80 backdrop-blur-sm border-[rgba(120,170,210,0.18)] hover:border-[#19B5FF] transition-all duration-300 hover-lift group"
              >
                <CardContent className="p-7">
                  <industry.icon className="w-11 h-11 text-[#19B5FF] mb-5 group-hover:scale-110 transition-transform duration-300 stroke-[1.5]" />
                  <h3 className="text-[#EAF4FF] font-bold text-base mb-4 leading-tight">{industry.name}</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-[#19B5FF] text-[11px] font-bold uppercase tracking-wider mb-2">Challenge</p>
                      <p className="text-[#A9BDD1] text-sm leading-[1.6]">{industry.problem}</p>
                    </div>
                    <div>
                      <p className="text-[#19B5FF] text-[11px] font-bold uppercase tracking-wider mb-2">Our Approach</p>
                      <p className="text-[#A9BDD1] text-sm leading-[1.6]">{industry.solution}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 05. SELECTED WORK */}
      <section id="work" className="py-32 bg-[#06121E] relative overflow-hidden">
        {/* Background Visual */}
        <div className="absolute left-0 top-0 bottom-0 w-1/2 opacity-[0.04]">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                'url(https://images.unsplash.com/photo-1720962158858-5fb16991d2b8)',
            }}
          ></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            <span className="text-[#19B5FF] font-bold text-xs uppercase tracking-[0.2em] mb-4 block">05. Selected Work</span>
            <h2 className="text-[#EAF4FF] font-extrabold mt-2 mb-6 leading-[1.1]">Proof of Capability</h2>
            <p className="text-[#A9BDD1] text-lg max-w-2xl mx-auto leading-relaxed">
              Representative examples of structured delivery across identity, publishing, creative, and analytics.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {selectedWork.map((work, index) => (
              <Card
                key={index}
                className="bg-gradient-to-br from-[#10263A]/90 to-[#0B1B2B]/90 backdrop-blur-sm border-[rgba(120,170,210,0.18)] hover:border-[#19B5FF] transition-all duration-300 hover-lift"
              >
                <CardContent className="p-9">
                  <h3 className="text-[#EAF4FF] font-bold text-xl mb-7 leading-tight">{work.title}</h3>
                  <div className="space-y-5">
                    <div>
                      <p className="text-[#19B5FF] text-xs font-bold uppercase tracking-wider mb-3">Situation</p>
                      <p className="text-[#A9BDD1] text-[15px] leading-[1.7]">{work.situation}</p>
                    </div>
                    <div>
                      <p className="text-[#19B5FF] text-xs font-bold uppercase tracking-wider mb-3">What We Did</p>
                      <p className="text-[#A9BDD1] text-[15px] leading-[1.7]">{work.action}</p>
                    </div>
                    <div>
                      <p className="text-[#19B5FF] text-xs font-bold uppercase tracking-wider mb-3">Result</p>
                      <p className="text-[#A9BDD1] text-[15px] leading-[1.7]">{work.result}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 06. ABOUT JIXDO */}
      <section id="about" className="py-32 bg-[#0B1B2B] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#06121E]/40 via-transparent to-[#10263A]/20"></div>

        <div className="max-w-6xl mx-auto px-6 lg:px-8 relative z-10">
          {/* Section header */}
          <div className="max-w-3xl mb-16">
            <span className="text-[#19B5FF] font-bold text-xs uppercase tracking-[0.2em] mb-4 block">06. About Jixdo</span>
            <h2 className="text-[#EAF4FF] font-extrabold mt-2 mb-8 leading-[1.1]">Structured, Multidisciplinary Practice</h2>
            <p className="text-[#A9BDD1] text-lg leading-[1.75] mb-6 font-light">
              Jixdo is a structured practice that integrates technology, publishing, accessibility, creative, and marketing
              operations into a single operating partner.
            </p>
            <p className="text-[#A9BDD1] leading-[1.7] font-light">
              We serve enterprise and upper mid-market clients across India, GCC, UK, and North America. Our method is
              built on standards (WCAG 2.2, PDF/UA, EPUB Accessibility 1.1, EAA), structured delivery frameworks, and
              cross-functional teams that understand both technical architecture and business outcomes.
            </p>
          </div>

          {/* Why Jixdo — editorial 4-column grid */}
          <div className="border-t border-[rgba(120,170,210,0.18)] pt-12">
            <p className="text-[#19B5FF] font-bold text-xs uppercase tracking-[0.2em] mb-10">Why Jixdo</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-10">
              {[
                {
                  title: 'One operating partner',
                  desc: 'Across identity, cloud, publishing, accessibility, creative, and marketing.',
                },
                {
                  title: 'Standards-based delivery',
                  desc: 'WCAG 2.2, PDF/UA, EPUB Accessibility 1.1, EAA, and Zero Trust frameworks.',
                },
                {
                  title: 'Multi-region footprint',
                  desc: 'India, GCC, United Kingdom, and North America.',
                },
                {
                  title: 'Technical depth, business clarity',
                  desc: 'Architecture-grade thinking translated into outcomes—no jargon walls.',
                },
              ].map((item, i) => (
                <div key={i} className="border-l-2 border-[#19B5FF]/40 pl-5">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-[#19B5FF] font-bold mb-3 block">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="text-[#EAF4FF] font-bold text-base mb-3 leading-tight">{item.title}</h3>
                  <p className="text-[#A9BDD1] text-sm leading-[1.7]">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 07. ENGAGE / CONTACT */}
      <section id="contact" className="py-32 bg-[#06121E] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(25,181,255,0.1),transparent_50%)]"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            <span className="text-[#19B5FF] font-bold text-xs uppercase tracking-[0.2em] mb-4 block">07. Engage</span>
            <h2 className="text-[#EAF4FF] font-extrabold mt-2 mb-6 leading-[1.1]">Discuss Your Project</h2>
            <p className="text-[#A9BDD1] text-lg max-w-2xl mx-auto leading-relaxed">
              Whether you need identity modernisation, accessible publishing, or brand and marketing infrastructure—let's
              start the conversation.
            </p>
          </div>

          {/* Response Time Callout */}
          <div className="max-w-3xl mx-auto mb-16">
            <div className="flex items-center justify-center gap-4 px-6 py-4 bg-[#10263A]/60 backdrop-blur-sm border border-[rgba(120,170,210,0.18)] rounded-full">
              <div className="w-2 h-2 rounded-full bg-[#19B5FF] animate-pulse"></div>
              <p className="text-sm text-[#A9BDD1]">
                Average response time: <span className="text-[#EAF4FF] font-semibold">within two working days</span>
              </p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-10">
            {/* Left Column: Form + Process */}
            <div className="space-y-6">
              {/* Contact Form */}
              <div className="bg-gradient-to-br from-[#10263A]/60 to-[#0B1B2B]/60 backdrop-blur-sm border border-[rgba(120,170,210,0.18)] rounded-2xl p-8 lg:p-10">
                <div className="mb-8">
                  <p className="text-[#19B5FF] text-xs font-bold uppercase tracking-[0.2em] mb-3">Project Inquiry</p>
                  <h3 className="text-[#EAF4FF] font-bold text-2xl leading-tight">Tell us about your engagement</h3>
                </div>
                <ContactForm />
              </div>

              {/* What Happens Next */}
              <Card className="bg-gradient-to-br from-[#10263A]/60 to-[#0B1B2B]/60 backdrop-blur-sm border-[rgba(120,170,210,0.18)]">
                <CardContent className="p-8 lg:p-10">
                  <p className="text-[#19B5FF] text-xs font-bold uppercase tracking-[0.2em] mb-5">What Happens Next</p>
                  <div className="space-y-5">
                    {[
                      { step: '01', title: 'Initial review', desc: 'We review your inquiry and align on relevant disciplines.' },
                      { step: '02', title: 'Discovery call', desc: 'A 45-minute call to understand scope, context, and constraints.' },
                      { step: '03', title: 'Structured proposal', desc: 'A clear engagement plan with phases, deliverables, and timing.' },
                    ].map((item, i) => (
                      <div key={i} className="flex gap-5 items-start">
                        <span className="text-[#19B5FF] font-bold text-base tracking-tight flex-shrink-0 w-8">{item.step}</span>
                        <div className="flex-1 pb-5 border-b border-[rgba(120,170,210,0.12)] last:border-0 last:pb-0">
                          <p className="text-[#EAF4FF] font-semibold text-[15px] mb-1.5 leading-tight">{item.title}</p>
                          <p className="text-[#A9BDD1] text-sm leading-[1.6]">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information & Map */}
            <div className="space-y-6">
              {/* Headquarters */}
              <Card className="bg-gradient-to-br from-[#10263A]/60 to-[#0B1B2B]/60 backdrop-blur-sm border-[rgba(120,170,210,0.18)]">
                <CardContent className="p-8 lg:p-10">
                  <div className="mb-7">
                    <p className="text-[#19B5FF] text-xs font-bold uppercase tracking-[0.2em] mb-3">Headquarters</p>
                    <h3 className="text-[#EAF4FF] font-bold text-2xl leading-tight">Jixdo · Noida, India</h3>
                  </div>

                  <address className="not-italic space-y-6">
                    {/* Address */}
                    <div className="border-l-2 border-[#19B5FF]/50 pl-5">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[#19B5FF] font-bold mb-2">Registered Address</p>
                      <p className="text-[#EAF4FF] text-[15px] font-medium leading-[1.7]">
                        D-107, 4th floor, Sector 2<br />
                        Noida — 201301<br />
                        Uttar Pradesh, India
                      </p>
                    </div>

                    {/* Email */}
                    <div className="border-l-2 border-[#19B5FF]/50 pl-5">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[#19B5FF] font-bold mb-2">General Inquiries</p>
                      <a
                        href="mailto:info@jixdo.com"
                        className="inline-flex items-center gap-2 text-[#EAF4FF] hover:text-[#19B5FF] text-[15px] font-medium transition-colors group"
                      >
                        <MailIcon className="w-4 h-4 text-[#19B5FF] stroke-[1.5]" />
                        info@jixdo.com
                      </a>
                    </div>

                    {/* Operating Regions */}
                    <div className="border-l-2 border-[#19B5FF]/50 pl-5">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[#19B5FF] font-bold mb-3">Operating Regions</p>
                      <div className="flex flex-wrap gap-2">
                        {['India', 'GCC', 'United Kingdom', 'North America'].map((region) => (
                          <span
                            key={region}
                            className="px-3 py-1 text-xs font-medium text-[#A9BDD1] border border-[rgba(120,170,210,0.18)] rounded-full"
                          >
                            {region}
                          </span>
                        ))}
                      </div>
                    </div>
                  </address>
                </CardContent>
              </Card>

              {/* Custom Global Map Composition */}
              <div className="aspect-[16/9] rounded-2xl overflow-hidden border border-[rgba(120,170,210,0.18)] bg-gradient-to-br from-[#0B1B2B] via-[#06121E] to-[#10263A] relative">
                {/* Soft grid overlay */}
                <div
                  className="absolute inset-0 opacity-[0.07] pointer-events-none"
                  style={{
                    backgroundImage:
                      'linear-gradient(rgba(25,181,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(25,181,255,0.4) 1px, transparent 1px)',
                    backgroundSize: '40px 40px',
                  }}
                ></div>

                {/* Radial vignette */}
                <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 50% 50%, transparent 30%, rgba(6,18,30,0.6) 100%)' }}></div>

                {/* Actual world map */}
                <div className="absolute inset-0 p-4">
                  <WorldMap />
                </div>

                {/* Corner labels */}
                <div className="absolute top-5 left-5 flex items-center gap-2 z-10">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#19B5FF] opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#19B5FF]"></span>
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.3em] text-[#A9BDD1] font-bold">Global Coverage</span>
                </div>
                <div className="absolute bottom-5 right-5 text-right z-10">
                  <p className="text-[9px] uppercase tracking-[0.3em] text-[#A9BDD1]">Operating Across</p>
                  <p className="text-[#EAF4FF] font-bold text-xs tracking-wide mt-1">4 Regions · 24/7 Delivery</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      </main>

      {/* FOOTER */}
      <footer className="bg-[#0B1B2B] border-t border-[rgba(120,170,210,0.18)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-10">
          {/* Top Grid */}
          <div className="grid lg:grid-cols-12 gap-10 mb-16">
            {/* Brand Column */}
            <div className="lg:col-span-5">
              <div className="flex items-center space-x-3 mb-5">
                <img
                  src="https://customer-assets.emergentagent.com/job_09644332-5e2e-4361-bc30-b66a2a3d7f1d/artifacts/qxe7l1b6_Jixdo_Monogram.png"
                  alt="Jixdo logo — multidisciplinary enterprise operating partner"
                  className="h-10 w-10"
                />
                <span className="text-2xl font-extrabold text-[#EAF4FF] tracking-tight">Jixdo</span>
              </div>
              <p className="text-[#A9BDD1] text-[15px] leading-[1.7] max-w-md font-light">
                A multidisciplinary practice integrating technology, identity, publishing, accessibility, creative, and
                marketing operations into one structured partner.
              </p>

              {/* Contact details */}
              <address className="not-italic mt-7 space-y-4">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#19B5FF] font-bold mb-2">Headquarters</p>
                  <p className="text-[#A9BDD1] text-sm leading-[1.7]">
                    D-107, 4th floor, Sector 2<br />
                    Noida — 201301, Uttar Pradesh, India
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#19B5FF] font-bold mb-2">Email</p>
                  <a
                    href="mailto:info@jixdo.com"
                    className="text-[#EAF4FF] hover:text-[#19B5FF] text-sm font-medium transition-colors"
                  >
                    info@jixdo.com
                  </a>
                </div>
              </address>

              {/* Region Tags */}
              <div className="mt-7">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#19B5FF] font-bold mb-3">Operating Regions</p>
                <div className="flex flex-wrap gap-2">
                  {['India', 'GCC', 'United Kingdom', 'North America'].map((region) => (
                    <span
                      key={region}
                      className="px-3 py-1.5 text-xs font-medium text-[#A9BDD1] border border-[rgba(120,170,210,0.18)] rounded-full hover:border-[#19B5FF]/50 hover:text-[#EAF4FF] transition-all"
                    >
                      {region}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Navigation Columns */}
            <div className="lg:col-span-3">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#19B5FF] font-bold mb-5">Navigate</p>
              <ul className="space-y-3">
                {[
                  { label: 'Services', href: '#services' },
                  { label: 'Capabilities', href: '#capabilities' },
                  { label: 'Industries', href: '#industries' },
                  { label: 'Selected Work', href: '#work' },
                  { label: 'About', href: '#about' },
                ].map((link) => (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      className="text-[#A9BDD1] hover:text-[#19B5FF] text-sm transition-colors inline-flex items-center gap-2 group"
                    >
                      <span className="w-0 group-hover:w-3 h-px bg-[#19B5FF] transition-all duration-300"></span>
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Standards Column */}
            <div className="lg:col-span-4">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#19B5FF] font-bold mb-5">Standards & Compliance</p>
              <ul className="space-y-3 text-[#A9BDD1] text-sm">
                {[
                  'WCAG 2.2 AA',
                  'PDF/UA',
                  'EPUB Accessibility 1.1',
                  'European Accessibility Act (EAA)',
                  'Zero Trust Architecture',
                ].map((standard) => (
                  <li key={standard} className="flex items-center gap-3">
                    <div className="w-1 h-1 rounded-full bg-[#19B5FF]"></div>
                    <span>{standard}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <Separator className="bg-[rgba(120,170,210,0.18)]" />

          {/* Bottom Bar */}
          <div className="flex flex-col md:flex-row justify-between items-center pt-8 gap-4">
            <p className="text-[#A9BDD1] text-xs tracking-wide">
              © {new Date().getFullYear()} Jixdo. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <a href="#" className="text-[#A9BDD1] hover:text-[#19B5FF] text-xs transition-colors">
                Privacy Policy
              </a>
              <span className="w-px h-3 bg-[rgba(120,170,210,0.18)]"></span>
              <a href="#" className="text-[#A9BDD1] hover:text-[#19B5FF] text-xs transition-colors">
                Terms of Service
              </a>
              <span className="w-px h-3 bg-[rgba(120,170,210,0.18)]"></span>
              <a href="#" className="text-[#A9BDD1] hover:text-[#19B5FF] text-xs transition-colors">
                Cookies
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;